import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import './MarketNews.css';

const MARKET_KEY = '__market__';
const STORAGE_ACTIVE = 'mn_active_ticker';

function MarketNews() {
  // ── Watchlist + selection state ─────────────────────────────────────
  const [watchlist, setWatchlist] = useState([]);
  // Live quotes for every watchlist ticker — keyed by ticker for O(1) row lookup.
  // Refreshed on a 60s timer (matches the news-fetch cadence) so the sidebar stays current
  // without hammering Tradier.
  const [quotes, setQuotes] = useState({}); // { AAPL: { price, change_pct }, ... }
  const [activeTicker, setActiveTicker] = useState(() => {
    return localStorage.getItem(STORAGE_ACTIVE) || MARKET_KEY;
  });
  const [newTickerInput, setNewTickerInput] = useState('');
  const [addError, setAddError] = useState('');

  // ── News data state (reflects whatever is currently selected) ──────
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(() => {
    const now = new Date();
    const h = parseInt(now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles', hour: 'numeric', hour12: false }));
    return h >= 6 && h < 13;
  });

  // Track the latest activeTicker inside callbacks (so timers always pick the
  // current selection without us having to re-create the interval).
  const activeRef = useRef(activeTicker);
  useEffect(() => { activeRef.current = activeTicker; }, [activeTicker]);

  // ── Endpoint helpers ────────────────────────────────────────────────
  const isMarket = (key) => key === MARKET_KEY;
  const fetchUrl = (key) => isMarket(key) ? '/api/news' : `/api/news/ticker/${encodeURIComponent(key)}`;
  const scanUrl = (key) => isMarket(key) ? '/api/news/scan' : `/api/news/ticker/${encodeURIComponent(key)}/scan`;
  const displayName = (key) => isMarket(key) ? 'Market News' : `${key} News`;

  // ── Watchlist loading ───────────────────────────────────────────────
  const loadWatchlist = useCallback(async () => {
    try {
      const res = await axios.get('/api/watchlist');
      setWatchlist(res.data || []);
    } catch (err) {
      console.error('watchlist load failed:', err);
    }
  }, []);

  // Live-quote fetcher for sidebar rows. Returns a dict { ticker: { price, change_pct } }.
  // Errors are swallowed and logged so a transient Tradier hiccup doesn't blank the UI.
  const loadQuotes = useCallback(async () => {
    try {
      const res = await axios.get('/api/watchlist/quotes');
      setQuotes(res.data || {});
    } catch (err) {
      console.error('quotes load failed:', err);
    }
  }, []);

  useEffect(() => { loadWatchlist(); }, [loadWatchlist]);

  // Refresh sidebar quotes whenever the watchlist changes (so a newly-added ticker
  // gets its price right away), plus every 60s to keep prices fresh during market hours.
  useEffect(() => {
    if (watchlist.length === 0) { setQuotes({}); return; }
    loadQuotes();
    const id = setInterval(loadQuotes, 60 * 1000);
    return () => clearInterval(id);
  }, [watchlist, loadQuotes]);

  // ── News fetching (re-runs whenever activeTicker changes) ──────────
  const fetchData = useCallback(async (key = activeRef.current) => {
    try {
      setError(null);
      setLoading(true);
      const res = await axios.get(fetchUrl(key));
      setData(res.data);
    } catch (err) {
      setError(`Failed to load news: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const scanNow = useCallback(async (key = activeRef.current) => {
    try {
      setScanning(true);
      setError(null);
      const res = await axios.post(scanUrl(key));
      // If we're still on the same ticker, update the view; otherwise the user
      // moved on, so just leave their current view alone.
      if (activeRef.current === key) {
        setData(res.data);
      }
    } catch (err) {
      setError(`Scan failed: ${err.message}`);
    } finally {
      if (activeRef.current === key) setScanning(false);
    }
  }, []);

  // Whenever the selected ticker changes, persist + fetch its data
  useEffect(() => {
    localStorage.setItem(STORAGE_ACTIVE, activeTicker);
    fetchData(activeTicker);
  }, [activeTicker, fetchData]);

  // 60s background refresh of the cached data file (cheap)
  useEffect(() => {
    const id = setInterval(() => fetchData(activeRef.current), 60 * 1000);
    return () => clearInterval(id);
  }, [fetchData]);

  // 15min auto-scan against whatever is currently selected
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => scanNow(activeRef.current), 15 * 60 * 1000);
    return () => clearInterval(id);
  }, [autoRefresh, scanNow]);

  // ── Watchlist mutations ─────────────────────────────────────────────
  const addTicker = useCallback(async () => {
    const ticker = (newTickerInput || '').trim().toUpperCase();
    if (!ticker) {
      setAddError('Enter a ticker');
      return;
    }
    setAddError('');
    try {
      await axios.post('/api/watchlist', { ticker });
      await loadWatchlist();
      setNewTickerInput('');
      setActiveTicker(ticker);
      // Kick off a scan immediately so the user sees data instead of an empty shell
      scanNow(ticker);
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setAddError(msg);
    }
  }, [newTickerInput, loadWatchlist, scanNow]);

  const removeTicker = useCallback(async (ticker) => {
    try {
      await axios.delete(`/api/watchlist/${encodeURIComponent(ticker)}`);
      await loadWatchlist();
      if (activeRef.current === ticker) setActiveTicker(MARKET_KEY);
    } catch (err) {
      console.error('remove failed:', err);
    }
  }, [loadWatchlist]);

  // ── Formatting helpers ──────────────────────────────────────────────
  const formatTime = (isoStr) => {
    if (!isoStr) return '';
    try {
      return new Date(isoStr).toLocaleString('en-US', {
        timeZone: 'America/Los_Angeles',
        hour: 'numeric', minute: '2-digit', hour12: true,
      });
    } catch { return ''; }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const [y, m, d] = dateStr.split('-');
      return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
    } catch { return dateStr; }
  };

  const lastScanFormatted = data?.last_scan
    ? new Date(data.last_scan).toLocaleString('en-US', {
        timeZone: 'America/Los_Angeles',
        month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
      })
    : 'Never';

  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
  const articles = data?.articles || [];
  const summary = data?.summary || null;
  const profile = data?.profile || null;
  const todayArticles = articles.filter(a => a.published === today);
  const olderArticles = articles.filter(a => a.published !== today);

  const olderByDate = {};
  olderArticles.forEach(a => {
    const d = a.published || 'Unknown';
    if (!olderByDate[d]) olderByDate[d] = [];
    olderByDate[d].push(a);
  });
  const olderDates = Object.keys(olderByDate).sort().reverse();

  const categoryBadge = (cat) => {
    switch (cat) {
      case 'economic': return 'mn-badge-economic';
      case 'financial': return 'mn-badge-financial';
      case 'world': return 'mn-badge-world';
      default: return 'mn-badge-financial';
    }
  };

  const categoryIcon = (cat) => {
    switch (cat) {
      case 'economic': return '📊';
      case 'world': return '🌍';
      default: return '💹';
    }
  };

  const renderTags = (tags) => {
    if (!tags || tags.length === 0) return null;
    return tags.map((tag, i) => (
      <span key={i} className={`mn-badge mn-badge-tag-${tag}`}>{tag}</span>
    ));
  };

  const sentimentClass = (lean) => {
    if (lean === 'positive') return 'mn-sentiment-pos';
    if (lean === 'negative') return 'mn-sentiment-neg';
    if (lean === 'mixed') return 'mn-sentiment-mixed';
    return 'mn-sentiment-neutral';
  };

  // Per-card sentiment badge. Compact: arrow + word. Bullish=green/up, bearish=red/down.
  // Neutral articles get no badge so the card stays uncluttered (the visual cue is
  // reserved for articles with directional signal).
  const renderArticleSentiment = (article) => {
    const lean = article?.sentiment?.lean;
    if (!lean || lean === 'neutral') return null;
    const isPos = lean === 'positive';
    return (
      <span className={`mn-badge mn-sentiment-badge ${isPos ? 'mn-sentiment-badge-pos' : 'mn-sentiment-badge-neg'}`}>
        {isPos ? '▲ bullish' : '▼ bearish'}
      </span>
    );
  };

  // ── Profile card helpers ────────────────────────────────────────────
  const fmtPrice = (n) => (typeof n === 'number' ? `$${n.toFixed(2)}` : '—');
  const fmtPct = (n) => (typeof n === 'number' ? `${n > 0 ? '+' : ''}${n.toFixed(1)}%` : '—');
  const fmtMarketCap = (mc) => {
    if (typeof mc !== 'number' || mc <= 0) return '—';
    if (mc >= 1e12) return `$${(mc / 1e12).toFixed(2)}T`;
    if (mc >= 1e9) return `$${(mc / 1e9).toFixed(2)}B`;
    if (mc >= 1e6) return `$${(mc / 1e6).toFixed(0)}M`;
    return `$${mc.toFixed(0)}`;
  };
  const recommendationLabel = (key) => {
    switch ((key || '').toLowerCase()) {
      case 'strong_buy': return 'Strong Buy';
      case 'buy': return 'Buy';
      case 'hold': return 'Hold';
      case 'sell': return 'Sell';
      case 'strong_sell': return 'Strong Sell';
      default: return null;
    }
  };
  const recommendationClass = (key) => {
    const k = (key || '').toLowerCase();
    if (k === 'strong_buy' || k === 'buy') return 'mn-rec-bull';
    if (k === 'sell' || k === 'strong_sell') return 'mn-rec-bear';
    return 'mn-rec-neutral';
  };

  const renderRiskDots = (level) => {
    const n = Math.max(0, Math.min(5, Number(level) || 0));
    // Risk color: 1-2 green, 3 yellow, 4-5 red
    const filledClass = n >= 4 ? 'mn-risk-dot-high' : n === 3 ? 'mn-risk-dot-med' : 'mn-risk-dot-low';
    return (
      <div className="mn-risk-dots">
        {[1, 2, 3, 4, 5].map(i => (
          <span key={i} className={`mn-risk-dot ${i <= n ? filledClass : 'mn-risk-dot-empty'}`} />
        ))}
      </div>
    );
  };

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div className="mn-page">
      {/* Page-level header — spans full width, sits above the sidebar+content row */}
      <div className="mn-header">
        <div className="mn-header-left">
          <h2 className="mn-section-heading">{displayName(activeTicker)}</h2>
          <span className="mn-computed-badge">
            {articles.length} articles
          </span>
        </div>
        <div className="mn-header-right">
          <span className="mn-date">Last scan: {lastScanFormatted}</span>
          <label className="mn-auto-toggle">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto 15m
          </label>
          <button
            className="mn-refresh-btn"
            onClick={() => scanNow()}
            disabled={scanning}
          >
            <svg className={scanning ? 'mn-spin' : ''} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            {scanning ? 'Scanning...' : 'Scan Now'}
          </button>
        </div>
      </div>

      <div className="mn-layout">
        {/* ─── Sidebar ─── */}
        <aside className="mn-sidebar">
          <div className="mn-sidebar-section">
            <h3 className="mn-sidebar-heading">Watchlist</h3>
            <ul className="mn-watchlist">
              <li
                className={`mn-watch-item ${activeTicker === MARKET_KEY ? 'mn-watch-active' : ''}`}
                onClick={() => setActiveTicker(MARKET_KEY)}
              >
                <span className="mn-watch-label">📈 Market</span>
              </li>
              {watchlist.map((w) => {
                const q = quotes[w.ticker];
                const hasPct = q && typeof q.change_pct === 'number';
                return (
                  <li
                    key={w.ticker}
                    className={`mn-watch-item ${activeTicker === w.ticker ? 'mn-watch-active' : ''}`}
                    onClick={() => setActiveTicker(w.ticker)}
                  >
                    <span className="mn-watch-label">{w.ticker}</span>
                    {q && (
                      <span className="mn-watch-quote">
                        {typeof q.price === 'number' && (
                          <span className="mn-watch-price">{fmtPrice(q.price)}</span>
                        )}
                        {hasPct && (
                          <span className={`mn-watch-pct ${q.change_pct >= 0 ? 'mn-delta-pos' : 'mn-delta-neg'}`}>
                            {fmtPct(q.change_pct)}
                          </span>
                        )}
                      </span>
                    )}
                    <button
                      className="mn-watch-delete"
                      onClick={(e) => { e.stopPropagation(); removeTicker(w.ticker); }}
                      title={`Remove ${w.ticker}`}
                    >
                      ✕
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="mn-sidebar-section mn-sidebar-add">
            <input
              type="text"
              value={newTickerInput}
              onChange={(e) => setNewTickerInput(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === 'Enter') addTicker(); }}
              placeholder="Add ticker (e.g. AAPL)"
              className="mn-add-input"
              maxLength={8}
            />
            <button className="mn-add-btn" onClick={addTicker}>+ Add</button>
            {addError && <div className="mn-add-error">{addError}</div>}
          </div>

          {/* ─── Ticker profile card — lives in the sidebar, just below the Add input.
              The narrow-column variant (.mn-profile inside .mn-sidebar) stacks each
              row label-above-value to keep everything readable at 240px width. ─── */}
          {!isMarket(activeTicker) && profile && (
            <div className="mn-sidebar-section mn-sidebar-profile">
              <div className="mn-profile">
                <div className="mn-profile-head">
                  <div className="mn-profile-titles">
                    <h2 className="mn-profile-ticker">{profile.ticker || activeTicker}</h2>
                    {profile.company_name && (
                      <div className="mn-profile-company">{profile.company_name}</div>
                    )}
                  </div>
                  {recommendationLabel(profile.recommendation) && (
                    <span className={`mn-profile-tag ${recommendationClass(profile.recommendation)}`}>
                      {recommendationLabel(profile.recommendation)}
                      {profile.analyst_count ? ` · ${profile.analyst_count}` : ''}
                    </span>
                  )}
                </div>

                <div className="mn-profile-grid">
                  <div className="mn-profile-row">
                    <span className="mn-profile-label">Price</span>
                    <span className="mn-profile-value">
                      {fmtPrice(profile.price)}
                      {typeof profile.day_change_pct === 'number' && (
                        <span className={`mn-profile-delta ${profile.day_change_pct >= 0 ? 'mn-delta-pos' : 'mn-delta-neg'}`}>
                          {fmtPct(profile.day_change_pct)}
                        </span>
                      )}
                    </span>
                  </div>

                  <div className="mn-profile-row">
                    <span className="mn-profile-label">52-week range</span>
                    <span className="mn-profile-value">
                      {typeof profile.week_52_low === 'number' && typeof profile.week_52_high === 'number'
                        ? `${fmtPrice(profile.week_52_low)} – ${fmtPrice(profile.week_52_high)}`
                        : '—'}
                    </span>
                  </div>

                  {typeof profile.perf_52w_from_high_pct === 'number' && (
                    <div className="mn-profile-row">
                      <span className="mn-profile-label">From 52w high</span>
                      <span className={`mn-profile-value ${profile.perf_52w_from_high_pct < 0 ? 'mn-delta-neg' : 'mn-delta-pos'}`}>
                        {fmtPct(profile.perf_52w_from_high_pct)}
                      </span>
                    </div>
                  )}

                  <div className="mn-profile-row">
                    <span className="mn-profile-label">Analyst target</span>
                    <span className="mn-profile-value">
                      {typeof profile.analyst_target_mean === 'number' ? (
                        <>
                          <span className="mn-target-price">{fmtPrice(profile.analyst_target_mean)}</span>
                          {typeof profile.price === 'number' && profile.price > 0 && (
                            <span className={`mn-profile-delta ${profile.analyst_target_mean >= profile.price ? 'mn-delta-pos' : 'mn-delta-neg'}`}>
                              {fmtPct(((profile.analyst_target_mean - profile.price) / profile.price) * 100)}
                            </span>
                          )}
                        </>
                      ) : '—'}
                    </span>
                  </div>

                  <div className="mn-profile-row">
                    <span className="mn-profile-label">Market cap</span>
                    <span className="mn-profile-value">{fmtMarketCap(profile.market_cap)}</span>
                  </div>
                </div>

                {profile.risk_level != null && (
                  <div className="mn-profile-risk">
                    <span className="mn-profile-label">Risk level</span>
                    <div className="mn-risk-right">
                      {renderRiskDots(profile.risk_level)}
                      {profile.risk_label && (
                        <span className="mn-risk-label">{profile.risk_label}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </aside>

        {/* ─── Main pane ─── */}
        <main className="mn-main">
          {error && <div className="mn-error">{error}</div>}

          {/* ─── Sentiment summary — shown for BOTH market and ticker views, at the top ─── */}
          {summary && summary.article_count > 0 && (
            <div className="mn-summary">
              <div className="mn-summary-header">
                <span className="mn-summary-label">Quick Summary</span>
                <span className={`mn-summary-sentiment ${sentimentClass(summary.sentiment?.lean)}`}>
                  {summary.sentiment?.lean || 'neutral'}
                </span>
              </div>
              <div className="mn-summary-headline">{summary.headline}</div>
              {summary.tldr && (
                <div className="mn-summary-tldr">{summary.tldr}</div>
              )}

              {summary.topics?.length > 0 && (
                <div className="mn-summary-topics">
                  {summary.topics.map((t, i) => (
                    <span key={i} className="mn-topic-pill">{t}</span>
                  ))}
                </div>
              )}

              {summary.top_headlines?.length > 0 && (
                <ul className="mn-summary-headlines">
                  {summary.top_headlines.map((h, i) => (
                    <li key={i} className="mn-summary-headline-row">
                      <span className="mn-summary-source">{h.source}</span>
                      <a href={h.url} target="_blank" rel="noopener noreferrer" className="mn-summary-link">
                        {h.title}
                      </a>
                    </li>
                  ))}
                </ul>
              )}

              {summary.sources?.length > 0 && (
                <div className="mn-summary-sources">
                  <span className="mn-summary-sub">Top sources:</span>
                  {summary.sources.map((s, i) => (
                    <span key={i} className="mn-source-chip">{s.source} ({s.count})</span>
                  ))}
                </div>
              )}
            </div>
          )}

          {loading && <div className="mn-loading">Loading news...</div>}

          {!loading && articles.length === 0 && !error && (
            <div className="mn-empty">
              {isMarket(activeTicker) ? (
                <>No news articles yet. Click <strong>Scan Now</strong> to fetch the latest market news.</>
              ) : (
                <>No news scanned yet for <strong>{activeTicker}</strong>. Click <strong>Scan Now</strong> to fetch.</>
              )}
            </div>
          )}

          {/* ── Today's News — Featured Cards ── */}
          {todayArticles.length > 0 && (
            <div className="mn-section">
              <h3 className="mn-section-title">Today's News</h3>
              <div className="mn-today-grid">
                {todayArticles.map((article, i) => (
                  <a
                    key={i}
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mn-today-card"
                  >
                    <div className="mn-card-header">
                      <div className="mn-card-badges">
                        <span className={`mn-badge ${categoryBadge(article.category)}`}>
                          {categoryIcon(article.category)} {article.category}
                        </span>
                        {renderArticleSentiment(article)}
                        {renderTags(article.tags)}
                      </div>
                      <span className="mn-card-source">{article.source}</span>
                    </div>
                    <h3 className="mn-card-title">{article.title}</h3>
                    <p className="mn-card-snippet">{article.snippet}</p>
                    <div className="mn-card-footer">
                      <span className="mn-card-time">{formatTime(article.fetched_at)}</span>
                      <span className="mn-card-link">Read →</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* ── Older News — Compact List by Date ── */}
          {olderDates.length > 0 && (
            <div className="mn-section">
              <h3 className="mn-section-title">Past 7 Days</h3>
              {olderDates.map(date => (
                <div key={date} className="mn-day-group">
                  <div className="mn-day-header">
                    <span className="mn-day-label">{formatDate(date)}</span>
                    <span className="mn-day-count">{olderByDate[date].length} articles</span>
                  </div>
                  <div className="mn-context-card">
                    <div className="mn-context-grid">
                      {olderByDate[date].map((article, i) => (
                        <a
                          key={i}
                          href={article.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mn-context-row"
                        >
                          <div className="mn-row-left">
                            <span className={`mn-dot ${categoryBadge(article.category)}`} />
                            <div className="mn-row-content">
                              <span className="mn-row-title">{article.title}</span>
                              <span className="mn-row-snippet">{article.snippet}</span>
                            </div>
                          </div>
                          <div className="mn-row-meta">
                            <div className="mn-row-badges">
                              <span className={`mn-badge mn-badge-sm ${categoryBadge(article.category)}`}>
                                {article.category}
                              </span>
                              {renderArticleSentiment(article)}
                              {renderTags(article.tags)}
                            </div>
                            <span className="mn-row-source">{article.source}</span>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

        </main>
      </div>
    </div>
  );
}

export default MarketNews;
